import { useEffect, useRef } from "react";
import { useStoreSelector } from "../../../../../src/lib/store";
import { getLabActions } from "../../labActions";
import {
  clampPatternsWidth,
  PATTERNS_WIDTH_MAX,
  PATTERNS_WIDTH_MIN,
} from "../../lib/dockSizes";
import { useDockResize } from "../../lib/useDockResize";
import { patchUi, uiStore } from "../../store/uiStore";
import { ArrangeBody } from "./ArrangeBody";
import {
  dockAsideBase,
  dockFocusRing,
  dockPanel,
  dockPanelClose,
  dockPanelHeader,
  dockPanelTitle,
} from "./dockChrome";

export type ArrangeDockProps = {
  /**
   * When true, render only the panel body (RightSidebar owns the aside + resize).
   * When false/omitted, keep a standalone resizable aside (legacy).
   */
  embedded?: boolean;
};

/**
 * Right-rail Arrange dock — Renoise-style pattern × track matrix.
 * Prefer embedding in RightSidebar so Samples can share the column width.
 */
export function ArrangeDock({ embedded = false }: ArrangeDockProps) {
  const open = useStoreSelector(uiStore, s => s.patternsOpen);
  const storedWidth = useStoreSelector(uiStore, s => s.patternsWidth);
  const panelRef = useRef<HTMLDivElement>(null);

  const commitWidth = (next: number) => {
    patchUi({ patternsWidth: next });
    getLabActions()?.persistPanels({ patternsWidth: next });
  };

  const {
    size: width,
    dragging,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    nudge,
  } = useDockResize({
    storedSize: storedWidth,
    clamp: clampPatternsWidth,
    onCommit: commitWidth,
    axis: "x",
    edge: "start",
  });

  useEffect(() => {
    if (open) {
      panelRef.current?.focus();
    }
  }, [open]);

  function close(): void {
    patchUi({ patternsOpen: false });
    getLabActions()?.persistPanels({ patternsOpen: false });
    getLabActions()?.closeArrange?.();
    getLabActions()?.setStatus("Patterns closed.");
  }

  const panel = (
    <div
      ref={panelRef}
      className={`patterns-panel ${dockPanel}`}
      tabIndex={0}
      role="region"
      aria-label="Arrange"
      onMouseDown={event => {
        const target = event.target;
        if (
          target instanceof HTMLInputElement
          || target instanceof HTMLTextAreaElement
          || target instanceof HTMLSelectElement
        ) {
          return;
        }
        if (event.target === event.currentTarget
          || (event.target instanceof Element
            && event.target.closest(".patterns-panel-header"))) {
          panelRef.current?.focus({ preventScroll: true });
        }
      }}
    >
      <div className={`patterns-panel-header ${dockPanelHeader}`}>
        <div className={`patterns-panel-title ${dockPanelTitle}`}>Arrange</div>
        <button type="button" className={`patterns-panel-close ${dockPanelClose}`} onClick={close}>
          Close
        </button>
      </div>
      <ArrangeBody />
    </div>
  );

  if (embedded) {
    if (!open) {
      return null;
    }
    return panel;
  }

  return (
    <aside
      id="patterns-dock"
      className={`patterns-dock relative isolate ${dockAsideBase} ${dockFocusRing} shrink-0 max-[720px]:!w-full ${
        open ? "flex" : "hidden"
      } ${dragging ? "select-none" : ""}`}
      style={{ width }}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize Arrange"
        aria-valuemin={PATTERNS_WIDTH_MIN}
        aria-valuemax={PATTERNS_WIDTH_MAX}
        aria-valuenow={width}
        tabIndex={0}
        className={[
          "absolute top-0 bottom-0 left-0 z-40 w-1.5 cursor-col-resize touch-none",
          "bg-transparent hover:bg-lab-accent/35 active:bg-lab-accent/50",
          "max-[720px]:hidden",
          dragging ? "bg-lab-accent/40" : "",
        ].join(" ")}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={event => {
          const step = event.shiftKey ? 32 : 16;
          if (event.key === "ArrowLeft") {
            event.preventDefault();
            nudge(step);
          } else if (event.key === "ArrowRight") {
            event.preventDefault();
            nudge(-step);
          }
        }}
      />
      {panel}
    </aside>
  );
}
